import { useState, useEffect, useRef } from "react";
import { doc, onSnapshot, DocumentData } from "firebase/firestore";
import { db } from "../../config/firebase";

interface UseFirestoreDocumentListenerResult<T> {
  data: T | null;
  error: string | null;
}

/**
 * A reusable hook for listening to Firestore document changes.
 * Sets up an onSnapshot listener and handles cleanup automatically.
 *
 * @param path - The full Firestore document path (e.g., "collection/documentId")
 * @returns Object containing the document data and any error that occurred
 */
export function useFirestoreDocumentListener<T extends DocumentData>(
  path: string | null
): UseFirestoreDocumentListenerResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Clear state when path changes
    setData(null);
    setError(null);

    // Clean up previous listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (!path) {
      return;
    }

    try {
      // Set up new listener
      const docRef = doc(db, path);
      unsubscribeRef.current = onSnapshot(
        docRef,
        (snapshot) => {
          if (snapshot.exists()) {
            setData(snapshot.data() as T);
            setError(null);
          } else {
            setData(null);
            setError("Document not found");
          }
        },
        (err) => {
          setData(null);
          setError(err.message);
        }
      );

      // Cleanup function
      return () => {
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
      };
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to set up document listener"
      );
      return;
    }
  }, [path]);

  return { data, error };
}
